<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

interface Menu {
  name: string
  to: RouteLocationRaw
  isNew?: boolean
}

const pagesList: Menu[] = [
  { name: 'FAQ', to: { name: 'faq' } },
  { name: 'Ons Team', to: { name: 'ons-team' } },
  { name: 'Over \'t SpeelFabrik', to: { name: 'over-speelfabrik' } },
  { name: 'Kampen', to: { name: 'kampen' } },
  { name: 'Feestjes', to: { name: 'feesten' } },
  { name: 'Zwemmen', to: { name: 'zwemmen' } },
]

// Newsletter
const newsletterEmail = ref('')

const onSubscribe = () => {
  // TODO: Wire up to API / Supabase if needed
  if (!newsletterEmail.value)
    return

  // Placeholder behavior for now
  console.info('Subscribe:', newsletterEmail.value)
  newsletterEmail.value = ''
}
</script>

<template>
  <div class="footer">
    <div class="footer-top">
      <VContainer>
        <!-- Decorative elements -->
        <div class="decorative-shapes">
          <div class="shape shape-1" />
          <div class="shape shape-2" />
          <div class="shape shape-3" />
        </div>

        <!-- Footer Columns -->
        <VRow class="footer-columns">
          <!-- Info Column -->
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <div class="footer-section section-info">
              <h6 class="footer-section-title mb-6">
                <VIcon
                  icon="bx-info-circle"
                  size="18"
                  class="title-icon"
                />
                <span>Info</span>
              </h6>
              <ul class="footer-list">
                <li
                  v-for="(item, index) in pagesList"
                  :key="index"
                  class="footer-list-item"
                >
                  <VIcon
                    icon="bx-chevron-right"
                    size="18"
                    class="list-arrow-icon"
                  />
                  <NuxtLink
                    :to="item.to"
                    class="footer-link-new"
                  >
                    {{ item.name }}
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </VCol>

          <!-- Contacts Column -->
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <div class="footer-section section-contacts">
              <h6 class="footer-section-title mb-6">
                <VIcon
                  icon="bx-phone"
                  size="18"
                  class="title-icon"
                />
                <span>Contact</span>
              </h6>
              <div class="contact-info">
                <div class="contact-row">
                  <VIcon
                    icon="bx-buildings"
                    size="18"
                    class="contact-icon company"
                  />
                  <span>Langestraat 15b, 1910 Kampenhout</span>
                </div>
                <a
                  class="contact-row"
                  href="mailto:contact@tspeelfabrik.be"
                >
                  <VIcon
                    icon="bx-envelope"
                    size="18"
                    class="contact-icon email"
                  />
                  <span>contact@tspeelfabrik.be</span>
                </a>
                <a
                  class="contact-row"
                  href="tel:+474681052"
                >
                  <VIcon
                    icon="bx-phone-call"
                    size="18"
                    class="contact-icon phone"
                  />
                  <span>+32 474 68 10 52</span>
                </a>
              </div>
            </div>
          </VCol>

          <!-- Newsletter Column -->
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <div class="footer-section section-newsletter">
              <h6 class="footer-section-title mb-6">
                <VIcon
                  icon="bx-envelope"
                  size="18"
                  class="title-icon"
                />
                <span>Blijf op de Hoogte</span>
              </h6>
              <div class="newsletter-mini">
                <VTextField
                  v-model="newsletterEmail"
                  type="email"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="newsletter-input"
                />
                <VBtn
                  block
                  class="newsletter-btn"
                  @click="onSubscribe"
                >
                  <VIcon
                    icon="bx-send"
                    size="16"
                    class="me-1"
                  />
                  Inschrijven
                </VBtn>
              </div>
            </div>
          </VCol>

          <!-- Logo Column -->
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <div class="footer-section section-logo">
              <!-- Social Media Section -->
              <div class="social-section mb-6">
                <h6 class="footer-section-title mb-6">
                  <VIcon
                    icon="bx-share-alt"
                    size="18"
                    class="title-icon"
                  />
                  <span>Volg ons</span>
                </h6>
                <div class="d-flex gap-3">
                  <VBtn
                    icon
                    size="small"
                    color="primary"
                    variant="tonal"
                    href="https://www.instagram.com/vzwtspeelfabrik/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Bezoek onze Instagram pagina"
                  >
                    <VIcon icon="bx-bxl-instagram" />
                  </VBtn>
                  <VBtn
                    icon
                    size="small"
                    color="primary"
                    variant="tonal"
                    href="https://www.facebook.com/tspeelfabrik"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Bezoek onze Facebook pagina"
                  >
                    <VIcon icon="bx-bxl-facebook" />
                  </VBtn>
                </div>
              </div>

              <div class="footer-brand d-flex align-items-center gap-3">
                <VNodeRenderer :nodes="themeConfig.app.logo" />
                <span class="brand-name">{{ themeConfig.app.title }}</span>
              </div>
            </div>
          </VCol>
        </VRow>
      </VContainer>
    </div>

    <!-- 👉 Footer Bottom -->
    <div class="footer-bottom w-100">
      <VContainer>
        <div
          class="d-flex justify-end flex-wrap gap-y-0 align-center py-0"
          style="padding-inline-end: 10%;"
        >
          <!-- Links -->
          <div class="footer-bottom-links d-flex gap-6 flex-wrap">
            <NuxtLink
              to="/legal/cookie-policy"
              class="bottom-link"
            >
              Cookiebeleid
            </NuxtLink>
            <span class="link-separator">•</span>
            <NuxtLink
              to="/legal/privacy-gdpr"
              class="bottom-link"
            >
              Privacy & AVG
            </NuxtLink>
            <span class="link-separator">•</span>
            <NuxtLink
              to="/legal/algemene-voorwaarden"
              class="bottom-link"
            >
              Algemene Voorwaarden
            </NuxtLink>
          </div>

          <!--
            Social Icons
            <div class="d-flex gap-3 align-items-center flex-wrap">
            <a
            v-for="(item, index) in [
            { title: 'facebook', icon: 'mdi-facebook', href: 'https://www.facebook.com/ThemeSelections/', color: '#9b87f5' },
            { title: 'twitter', icon: 'mdi-twitter', href: 'https://x.com/Theme_Selection', color: '#7dd3fc' },
            { title: 'youtube', icon: 'mdi-youtube', href: 'https://www.youtube.com', color: '#f472b6' },
            ]"
            :key="index"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
            class="social-circle"
            :style="{ 'background-color': item.color }"
            >
            <VIcon
            :icon="item.icon"
            size="18"
            color="white"
            />
            </a>
            </div>
          -->
        </div>
      </VContainer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.footer {
  position: relative;
  overflow: hidden;
}

.footer-hero-title {
  color: #e87b35;
  font-family: "Peachy Keen JF", "Public Sans", sans-serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  line-height: 1.2;
  margin-block-end: 1rem;
}

.footer-hero-subtitle {
  color: #8b6f47;
  font-family: "Peachy Keen JF", "Public Sans", sans-serif;
  font-size: 1.125rem;
  line-height: 1.6;
}

.character-illustration {
  display: inline-block;
  margin-block-start: 2rem;
}

.character-emoji {
  display: inline-block;
  animation: wave 3s ease-in-out infinite;
  font-size: 8rem;
}

@keyframes wave {
  0%,
  100% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
}

/* Newsletter Card */
.newsletter-card {
  border-radius: 24px;
  background: white;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 8%);
  padding-block: 2rem;
  padding-inline: 2rem;
}

/* Decorative elements */
.decorative-shapes {
  position: absolute;
  overflow: hidden;
  inset: 0;
  opacity: 0.6;
  pointer-events: none;
}

.shape {
  position: absolute;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  animation: morph 20s ease-in-out infinite;
  block-size: 150px;
  inline-size: 150px;
  opacity: 0.1;
}

.shape-1 {
  animation-delay: 0s;
  background: linear-gradient(135deg, #ffd4a3, #ffb84d);
  inset-block-start: 10%;
  inset-inline-start: 5%;
}

.shape-2 {
  animation-delay: 5s;
  background: linear-gradient(135deg, #ffc87c, #ff9a56);
  inset-block-start: 40%;
  inset-inline-end: 8%;
}

.shape-3 {
  animation-delay: 10s;
  background: linear-gradient(135deg, #ffe5b4, #ffc87c);
  inset-block-end: 30%;
  inset-inline-start: 10%;
}

@keyframes morph {
  0%,
  100% {
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    transform: translate(0, 0) rotate(0deg);
  }

  25% {
    border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%;
    transform: translate(10px, -10px) rotate(90deg);
  }

  50% {
    border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%;
    transform: translate(-10px, 10px) rotate(180deg);
  }

  75% {
    border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%;
    transform: translate(10px, 10px) rotate(270deg);
  }
}

.floating-ice-cream,
.floating-star,
.floating-heart {
  position: absolute;
  animation: float-gentle 6s ease-in-out infinite;
  font-size: 3rem;
}

.floating-ice-cream {
  animation-delay: 0s;
  inset-block-start: 15%;
  inset-inline-end: 5%;
}

.floating-star {
  animation-delay: 2s;
  inset-block-start: 50%;
  inset-inline-start: 3%;
}

.floating-heart {
  animation-delay: 4s;
  inset-block-end: 25%;
  inset-inline-end: 10%;
}

@keyframes float-gentle {
  0%,
  100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.decorative-flower {
  position: absolute;
  inset-block-end: 5%;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

.flower-emoji {
  display: inline-block;
  animation: spin-slow 30s linear infinite;
  font-size: 4rem;
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Footer Sections */
.footer-columns {
  margin-block-start: 3rem;
}

.footer-section {
  padding-inline: 1rem;
}

.footer-section-title {
  color: #2c3e50;
  font-family: "Peachy Keen JF", sans-serif !important;
  font-size: 1.25rem;
  font-weight: 700;
  margin-block-end: 1.5rem;

  span {
    font-family: "Peachy Keen JF", sans-serif !important;
  }
}

.v-theme--dark .footer-section-title {
  color: #e2e8f0;
}

.footer-section-description {
  color: #8b6f47;
  font-size: 0.9rem;
  line-height: 1.7;
}

.v-theme--dark .footer-section-description {
  color: #b89968;
}

/* Call Box */
.call-box {
  display: flex;
  align-items: center;
  border-radius: 20px;
  background: white;
  box-shadow: 0 4px 15px rgba(232, 123, 53, 15%);
  gap: 1rem;
  padding-block: 1rem;
  padding-inline: 1.25rem;
}

.v-theme--dark .call-box {
  background: rgba(255, 255, 255, 5%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 30%);
}

.call-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff9a56, #e87b35);
  block-size: 48px;
  inline-size: 48px;
}

.call-label {
  color: #8b6f47;
  font-size: 0.875rem;
  margin-block-end: 0.25rem;
}

.call-number {
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
}

.v-theme--dark .call-label {
  color: #b89968;
}

.v-theme--dark .call-number {
  color: #e2e8f0;
}

/* Footer Lists */
.footer-list {
  list-style: none;
  padding-inline-start: 0;
}

.footer-list-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-block-end: 0.75rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateX(5px);
  }
}

.list-arrow {
  color: #e87b35;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
}

.title-icon {
  color: #ff9a56;
  margin-inline-end: 0.5rem;
}

.list-arrow-icon {
  color: #ff9a56;
  margin-inline-end: 0.5rem;
}

.v-theme--dark .list-arrow {
  color: #ffb84d;
}

.footer-link-new {
  color: #8b6f47;
  font-size: 0.95rem;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #e87b35;
  }
}

.v-theme--dark .footer-link-new {
  color: #b89968;

  &:hover {
    color: #ffb84d;
  }
}

/* Warm icon colors per section */
.section-info .title-icon,
.section-info .list-arrow-icon {
  color: #ff9a56;
}

.section-contacts .title-icon {
  color: #e87b35;
}

.section-newsletter .title-icon {
  color: #ffb84d;
}

.section-admin .title-icon,
.section-admin .list-arrow-icon {
  color: #ffc87c;
}

/* News Items */
.news-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.news-image {
  display: flex;
  overflow: hidden;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(135deg, #fff9f0, #f0f9ff);
  block-size: 80px;
  inline-size: 80px;
}

.news-placeholder {
  font-size: 2rem;
}

.news-content {
  flex: 1;
}

.news-title {
  color: #2c3e50;
  font-family: "Peachy Keen JF", "Public Sans", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  margin-block-end: 0.5rem;
}

.news-excerpt {
  margin: 0;
  color: #8b6f47;
  font-size: 0.85rem;
  line-height: 1.5;
}

/* Footer Bottom */
.footer-bottom {
  position: relative;
  z-index: 2;
  background: #fef5ec;
  border-block-start: 1px solid #ffe5b4;
  padding-block: 0.5rem;
}

.v-theme--dark .footer-bottom {
  background: #1a1625;
  border-block-start: 1px solid rgba(255, 255, 255, 10%);
}

.footer-brand {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transform: translateY(-1rem);

  /* stylelint-disable selector-pseudo-class-no-unknown */
  :deep(img),
  :deep(svg) {
    block-size: 64px !important;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 10%));
    inline-size: auto !important;
    max-block-size: 64px;
  }
  /* stylelint-enable selector-pseudo-class-no-unknown */
}

.brand-name {
  color: #2c3e50;
  font-family: "Peachy Keen JF", "Public Sans", sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
}

.v-theme--dark .brand-name {
  color: #e2e8f0;
}

.footer-bottom-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
}

.bottom-link {
  color: #8b6f47;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #e87b35;
  }
}

.v-theme--dark .bottom-link {
  color: #b89968;

  &:hover {
    color: #ffb84d;
  }
}

/* Newsletter mini */
.newsletter-mini {
  display: grid;
  row-gap: 0.75rem;
}

.newsletter-input {
  font-size: 0.9rem;
}

.newsletter-btn {
  border-radius: 8px;
  background: #e87b35;
  color: white;
  font-family: "Peachy Keen JF", "Public Sans", sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding-block: 0.5rem;
  text-transform: none;
  transition: all 0.3s ease;

  &:hover {
    background: #ff9a56;
    box-shadow: 0 4px 12px rgba(232, 123, 53, 25%);
    transform: translateY(-1px);
  }
}

.v-theme--dark .newsletter-btn {
  background: #ff9a56;

  &:hover {
    background: #ffb84d;
  }
}

.link-separator {
  color: #ffe5b4;
}

.v-theme--dark .link-separator {
  color: #475569;
}

/* Contact info rows */
.contact-row {
  display: flex;
  align-items: center;
  color: #8b6f47;
  gap: 0.5rem;
  margin-block-end: 0.5rem;
  text-decoration: none;
}

.v-theme--dark .contact-row {
  color: #b89968;
}

.contact-icon {
  margin-inline-end: 0.25rem;
}

.contact-icon.company {
  color: #ffc87c;
}

.contact-icon.email {
  color: #ff9a56;
}

.contact-icon.phone {
  color: #e87b35;
}

/* Social Circles */
.social-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  block-size: 40px;
  inline-size: 40px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(232, 123, 53, 25%);
    transform: translateY(-3px) scale(1.1);
  }
}

/* Action Buttons */
.action-btn {
  border-radius: 20px !important;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding-inline: 1.5rem !important;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 20%);
    transform: translateY(-2px);
  }
}

.demos-btn {
  color: white !important;
}
</style>

<style lang="scss">
/* Newsletter Form */
.subscribe-form-new {
  display: flex;
  align-items: center;
  gap: 1rem;

  .subscribe-input-new {
    flex: 1;

    .v-field {
      border: 2px solid #ffe5b4;
      border-radius: 16px;
      background: white;
      box-shadow: none;
      transition: all 0.3s ease;

      &:hover,
      &.v-field--focused {
        border-color: #ffb84d;
        background: white;
      }

      input {
        color: #2c3e50;
        font-weight: 500;

        &::placeholder {
          color: #8b6f47;
        }
      }
    }
  }

  .send-btn {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ff9a56, #e87b35) !important;
    color: white !important;
    font-family: "Peachy Keen JF", "Public Sans", sans-serif;
    font-weight: 700;
    letter-spacing: 0.5px;
    min-inline-size: 120px;
    padding-block: 1.5rem !important;
    padding-inline: 2.5rem !important;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 10px 30px rgba(232, 123, 53, 40%);
      transform: translateY(-2px);
    }
  }
}

.footer {
  @media (min-width: 600px) and (max-width: 960px) {
    .v-container {
      padding-inline: 2rem !important;
    }
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .footer-hero-title {
    text-align: center;
  }

  .footer-hero-subtitle {
    text-align: center;
  }

  .character-illustration {
    inline-size: 100%;
    text-align: center;
  }

  .newsletter-card {
    margin-block-start: 2rem;
  }

  .subscribe-form-new {
    flex-direction: column;

    .send-btn {
      inline-size: 100%;
    }
  }

  .footer-section {
    margin-block-end: 2rem;
  }

  .decorative-shapes {
    opacity: 0.3;
  }

  .footer-bottom-links {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .floating-ice-cream,
  .floating-star,
  .floating-heart {
    display: none;
  }

  .decorative-flower {
    display: none;
  }
}
</style>
